import React from 'react';
import {formatArea, formatPercent, FORMULA_LABELS} from '../lib/calculations';
import {MIRROR, SYM, SIDE} from '../lib/bodyShapes';

const shortNames = {
  'Mosteller': 'Mosteller',
  'DuBois e DuBois': 'Du Bois',
  'Haycock': 'Haycock',
  'Gehan e George': 'Gehan',
  'Boyd': 'Boyd',
  'Fujimoto': 'Fujimoto',
  'Takahira': 'Takahira',
  'Wang': 'Wang',
  'Livingston e Lee': 'Livingston',
  'Schlich': 'Schlich'
};

export default function HeroVisual({metric, area, formula, results}) {
  const height = metric?.height ?? 170;
  const weight = metric?.weight ?? 70;
  const bodyHeight = Math.max(228, Math.min(320, 245 + (height - 150) * 0.72));
  const bodyWidth = Math.max(76, Math.min(116, 87 + ((weight / height) - (70 / 170)) * 100));
  const validResults = results ? FORMULA_LABELS.map(name => ({
    name,
    value: results[name]
  })).filter(item => Number.isFinite(item.value)) : [];
  const values = validResults.map(item => item.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const spread = min > 0 ? (max - min) / min * 100 : 0;

  return (
    <aside className="hero-visual" aria-label="Visualização dinâmica da superfície corporal">
      <div className="figure-stage">
        <div className="height-ruler" aria-label={`Altura informada: ${height.toLocaleString('pt-BR')} centímetros`}>
          <span>altura</span><strong>{height.toLocaleString('pt-BR')} cm</strong><i />
        </div>
        <svg className="person-figure" viewBox="38 0 108 274" preserveAspectRatio="none" role="img" aria-label={`Silhueta ilustrativa, proporcional às medidas informadas: ${height.toLocaleString('pt-BR')} centímetros e ${weight.toLocaleString('pt-BR')} quilogramas.`} style={{height: `${bodyHeight}px`, width: `${bodyWidth}px`}}>
          <defs>
            <linearGradient id="figure-fill" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" stopColor="#4a93c3" />
              <stop offset="0.55" stopColor="#387ba7" />
              <stop offset="1" stopColor="#2f6a91" />
            </linearGradient>
          </defs>
          <g className="figure-parts">
            <path d={SYM.head} /><path d={SYM.neck} /><path d={SYM.torso} />
            {Object.values({a: SIDE.arm, b: SIDE.forearm, c: SIDE.hand, d: SIDE.thigh, e: SIDE.leg, f: SIDE.foot}).map((d, i) => (
              <React.Fragment key={i}><path d={d} /><path d={d} transform={MIRROR} /></React.Fragment>
            ))}
          </g>
        </svg>
        <div className="square-reference" role="img" aria-label="Quadrado de referência com um metro de lado, equivalente a um metro quadrado">
          <span>1 m</span><strong>1 m²</strong><span>1 m</span>
        </div>
        <div className="figure-result" aria-live="polite">
          <span>superfície estimada · {formula}</span>
          <strong>{area == null ? '—' : formatArea(area)} <small>m²</small></strong>
        </div>
      </div>

      <div className="formula-spectrum">
        <div className="spectrum-heading">
          <strong>Como as dez fórmulas se distribuem?</strong>
          <span>Variação entre os resultados: {formatPercent(spread)}%</span>
        </div>
        <div className="spectrum-scroll" role="list" aria-label="Resultados das dez fórmulas em ordem crescente">
          {validResults.slice().sort((a, b) => a.value - b.value).map(item => {
            const position = max === min ? 50 : 8 + ((item.value - min) / (max - min)) * 84;
            return (
              <div className={`spectrum-point ${item.name === formula ? 'is-primary' : ''}`} role="listitem" key={item.name}>
                <span className="point-track" aria-hidden="true"><i style={{left: `${position}%`}} /></span>
                <strong>{shortNames[item.name]}</strong>
                <span>{formatArea(item.value)} m²</span>
              </div>
            );
          })}
        </div>
        <p>O destaque acompanha a fórmula escolhida. A distância entre os pontos mostra a variação, não qual estimativa é correta.</p>
      </div>
    </aside>
  );
}
